import type {
  AcceptedBotDefeatOutcome,
  BotDefeatRuntime,
} from "./bot-defeat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";

export interface BotRegenerationSnapshot {
  readonly activeCount: number;
  readonly generatedIds: readonly string[];
  readonly nextSpawnAtMs: number | null;
  readonly vacancies: number;
}

interface BotPort {
  getSnapshot(): { readonly bots: readonly { readonly id: string }[] };
  replenish(
    player: {
      readonly body: readonly {
        readonly position: { readonly x: number; readonly y: number };
        readonly radius: number;
      }[];
      readonly position: { readonly x: number; readonly y: number };
      readonly radius: number;
    } | null,
  ): string | null;
}
type DefeatPort = Pick<BotDefeatRuntime, "subscribe">;
type PlayerPort = Pick<PlayerRuntime, "getSnapshot" | "subscribeFixedStep">;

export class BotRegenerationRuntime {
  private activeRunId = 0;
  private destroyed = false;
  private readonly generatedIds: string[] = [];
  private nextSpawnAtMs: number | null = null;
  private readonly unsubscribeDefeat: () => void;
  private readonly unsubscribeFlow: () => void;
  private readonly unsubscribeStep: () => void;
  private vacancies = 0;

  public constructor(
    flow: GameFlow,
    defeats: DefeatPort,
    private readonly playerRuntime: PlayerPort,
    private readonly botRuntime: BotPort,
  ) {
    this.unsubscribeDefeat = defeats.subscribe((outcome) => {
      this.recordVacancy(outcome);
    });
    this.unsubscribeStep = playerRuntime.subscribeFixedStep(
      (_stepMilliseconds, simulationTimeMs) => {
        this.step(simulationTimeMs);
      },
    );
    this.unsubscribeFlow = flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.unsubscribeDefeat();
    this.unsubscribeStep();
    this.unsubscribeFlow();
    this.reset();
  }

  public getSnapshot(): BotRegenerationSnapshot {
    return {
      activeCount: this.botRuntime.getSnapshot().bots.length,
      generatedIds: [...this.generatedIds],
      nextSpawnAtMs: this.nextSpawnAtMs,
      vacancies: this.vacancies,
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    this.reset();
    if (snapshot.scene === "gameplay") {
      this.activeRunId = this.playerRuntime.getSnapshot().runId;
    }
  }

  private recordVacancy(outcome: AcceptedBotDefeatOutcome): void {
    if (this.destroyed || outcome.runId !== this.activeRunId) {
      return;
    }
    this.vacancies = this.currentVacancies();
    if (this.vacancies > 0 && this.nextSpawnAtMs === null) {
      this.nextSpawnAtMs =
        this.playerRuntime.getSnapshot().simulationTimeMs +
        gameplayConfig.bots.regenerationCadenceMs;
    }
  }

  private step(simulationTimeMs: number): void {
    if (
      this.destroyed ||
      this.activeRunId === 0 ||
      this.nextSpawnAtMs === null ||
      simulationTimeMs < this.nextSpawnAtMs
    ) {
      return;
    }
    this.vacancies = this.currentVacancies();
    if (this.vacancies === 0) {
      this.nextSpawnAtMs = null;
      return;
    }
    const playerSnapshot = this.playerRuntime.getSnapshot();
    const player = playerSnapshot.player;
    const identifier = this.botRuntime.replenish(
      player?.alive === true
        ? {
            body: playerSnapshot.body?.segments ?? [],
            position: player.position,
            radius: player.collision.radius,
          }
        : null,
    );
    if (identifier !== null) {
      this.generatedIds.push(identifier);
    }
    this.vacancies = this.currentVacancies();
    this.nextSpawnAtMs =
      this.vacancies === 0
        ? null
        : this.nextSpawnAtMs + gameplayConfig.bots.regenerationCadenceMs;
  }

  private currentVacancies(): number {
    return Math.max(
      0,
      gameplayConfig.bots.targetActive -
        this.botRuntime.getSnapshot().bots.length,
    );
  }

  private reset(): void {
    this.activeRunId = 0;
    this.generatedIds.length = 0;
    this.nextSpawnAtMs = null;
    this.vacancies = 0;
  }
}
